/**
 * @fileoverview Authenticated layout route for protected pages
 * @module routes/_authenticated
 *
 * Uses non-blocking AuthGuard pattern for better performance.
 * Auth checks happen client-side after initial render, showing
 * a skeleton while resolving. This improves TTFB and Core Web Vitals.
 */

import type { ReactElement } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { AuthGuard } from '@/features/auth/components/AuthGuard';
import { KOLDashboardSkeleton } from '@/features/dashboard/components/KOLDashboardSkeleton';

/**
 * Authenticated layout component.
 *
 * Wraps child routes with AuthGuard for non-blocking authentication.
 * The guard shows a skeleton while auth state loads, then either:
 * - Renders children (authenticated + onboarding complete)
 * - Redirects to / (not authenticated)
 * - Redirects to /onboarding (authenticated but needs onboarding)
 *
 * ## Onboarding Routing Schema
 *
 * ### Flow States:
 * - **Unauthenticated**: Redirect to home page
 * - **Authenticated, onboarding incomplete**: Redirect to /onboarding
 * - **Authenticated, onboarding complete**: Allow access to child routes
 *
 * ### Protected Routes:
 * - /dashboard - Main user dashboard
 * - /profile - User profile settings
 * - Any future protected routes added as children
 *
 * @component
 * @example
 * ```
 * User Flow Examples:
 * 1. New user: Login → /onboarding → Complete → /dashboard
 * 2. Returning user: Login → /dashboard
 * 3. Mid-onboarding: Login → /onboarding (resume from saved step)
 * ```
 */
function AuthenticatedLayout(): ReactElement {
  return (
    <AuthGuard fallback={<KOLDashboardSkeleton />}>
      <Outlet />
    </AuthGuard>
  );
}

/**
 * Authenticated layout route configuration.
 *
 * No beforeLoad is used - authentication is handled by AuthGuard
 * component for non-blocking performance. This allows the page
 * to render immediately with a skeleton while auth resolves.
 */
export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
});
